import { User, Account, Transaction, CreditCard, LoanApplication, Loan } from '../types';

// Mock Users
export const mockUsers: User[] = [
  {
    id: 'user-1',
    email: 'john.doe@email.com',
    password: 'password123',
    fullName: 'John Doe',
    mobileNumber: '+1-555-0123',
    address: {
      street: '123 Main St',
      city: 'New York',
      state: 'NY',
      zipCode: '10001'
    },
    role: 'customer',
    createdAt: new Date('2024-01-15')
  },
  {
    id: 'user-2',
    email: 'jane.smith@email.com',
    password: 'password123',
    fullName: 'Jane Smith',
    mobileNumber: '+1-555-0456',
    address: {
      street: '456 Oak Ave',
      city: 'Los Angeles',
      state: 'CA',
      zipCode: '90210'
    },
    role: 'customer',
    createdAt: new Date('2024-02-01')
  },
  {
    id: 'admin-1',
    email: 'admin@demobankltd.com',
    password: 'admin123',
    fullName: 'Bank Administrator',
    mobileNumber: '+1-555-9999',
    address: {
      street: '789 Bank Plaza',
      city: 'Chicago',
      state: 'IL',
      zipCode: '60601'
    },
    role: 'admin',
    createdAt: new Date('2024-01-01')
  }
];

// Mock Accounts
export const mockAccounts: Account[] = [
  {
    id: 'acc-1',
    userId: 'user-1',
    accountNumber: '1234567890',
    accountType: 'checking',
    balance: 5250.75,
    status: 'active',
    createdAt: new Date('2024-01-15')
  },
  {
    id: 'acc-2',
    userId: 'user-1',
    accountNumber: '1234567891',
    accountType: 'savings',
    balance: 12890.50,
    status: 'active',
    createdAt: new Date('2024-01-15')
  },
  {
    id: 'acc-3',
    userId: 'user-2',
    accountNumber: '2234567890',
    accountType: 'checking',
    balance: 3420.25,
    status: 'active',
    createdAt: new Date('2024-02-01')
  },
  {
    id: 'acc-4',
    userId: 'user-2',
    accountNumber: '2234567891',
    accountType: 'savings',
    balance: 8750.00,
    status: 'active',
    createdAt: new Date('2024-02-01')
  }
];

// Mock Transactions
export const mockTransactions: Transaction[] = [
  {
    id: 'txn-1',
    fromAccountId: 'acc-1',
    amount: 1200.00,
    type: 'deposit',
    description: 'Salary Deposit',
    timestamp: new Date('2024-03-01')
  },
  {
    id: 'txn-2',
    fromAccountId: 'acc-1',
    toAccountId: 'acc-2',
    amount: 500.00,
    type: 'transfer',
    description: 'Transfer to Savings',
    timestamp: new Date('2024-03-02')
  },
  {
    id: 'txn-3',
    fromAccountId: 'acc-1',
    toAccountId: 'acc-3',
    amount: 250.00,
    type: 'transfer',
    description: 'Payment to Jane Smith',
    timestamp: new Date('2024-03-03'),
    recipientEmail: 'jane.smith@email.com'
  },
  {
    id: 'txn-4',
    fromAccountId: 'acc-3',
    amount: 800.00,
    type: 'deposit',
    description: 'Freelance Payment',
    timestamp: new Date('2024-03-04')
  }
];

// Mock Credit Cards
export const mockCreditCards: CreditCard[] = [
  {
    id: 'cc-1',
    userId: 'user-1',
    cardNumber: '4532 **** **** 1234',
    cardType: 'platinum',
    creditLimit: 10000,
    currentBalance: 2350.75,
    status: 'active',
    expiryDate: '12/27',
    createdAt: new Date('2024-01-20')
  },
  {
    id: 'cc-2',
    userId: 'user-2',
    cardNumber: '4532 **** **** 5678',
    cardType: 'gold',
    creditLimit: 7500,
    currentBalance: 1250.00,
    status: 'active',
    expiryDate: '08/26',
    createdAt: new Date('2024-02-10')
  }
];

// Mock Loan Applications
export const mockLoanApplications: LoanApplication[] = [
  {
    id: 'loan-app-1',
    userId: 'user-1',
    loanType: 'personal',
    amount: 15000,
    purpose: 'Home improvement',
    status: 'pending',
    appliedAt: new Date('2024-03-01')
  },
  {
    id: 'loan-app-2',
    userId: 'user-2',
    loanType: 'auto',
    amount: 25000,
    purpose: 'Car purchase',
    status: 'pending',
    appliedAt: new Date('2024-03-02')
  }
];

// Mock Loans
export const mockLoans: Loan[] = [
  {
    id: 'loan-1',
    userId: 'user-1',
    applicationId: 'loan-app-3',
    accountId: 'acc-5',
    loanType: 'personal',
    principalAmount: 10000,
    currentBalance: 8500,
    interestRate: 6.5,
    termMonths: 36,
    monthlyPayment: 308.77,
    status: 'active',
    createdAt: new Date('2024-01-30')
  }
];

// Helper functions
export const generateAccountNumber = (): string => {
  return Math.floor(1000000000 + Math.random() * 9000000000).toString();
};

export const generateCardNumber = (): string => {
  const prefix = '4532';
  const end = Math.floor(1000 + Math.random() * 9000);
  return `${prefix} **** **** ${end}`;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const formatDate = (date: Date): string => {
  // Safety check for invalid dates
  if (!date || isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

export const calculateCreditUtilization = (balance: number, limit: number): number => {
  return (balance / limit) * 100;
}; 